import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  timer,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import {
  OrbiAgentWalletType,
  addPayment,
  addSelectedPaymentList,
  explainHowItWorks,
  getPatientWallet,
  loadPatientAppointments,
  logOut,
  retryLastMessage,
  selectChatLockedForSession,
  selectOrbiConversationStartedAt,
  selectOrbiMessages,
  selectOrbiSendPending,
  selectOrbiSessionId,
  selectOrbiThreadMessages,
  selectPatient,
  selectPatientAppointments,
  selectPatientAppointmentsRequestStatus,
  selectResultDetail,
  sendMessage,
  sendMessageFailure,
  sendMessageSuccess,
} from '@patient-ui/patient-web/store';
import {
  ORBI_APPOINTMENT_ACTION,
  ORBI_RETURN_TYPE,
  isReturnType,
} from '@patient-ui/shared/constants';
import { OrbiMessageType, RequestStatus } from '@patient-ui/shared/enums';
import { OrbiMessage } from '@patient-ui/shared/models';
import {
  OrbiInsuranceEligibilityMap,
  buildOrbiInsuranceEligibility,
  buildOrbiRequestContext,
  formatOrbiChatDate,
  orbiMessageLocksChat,
} from '@patient-ui/shared/utils';

import { APPOINTMENTS_IN_APP_NAVIGATION } from '../../../portal-appointments/appointments.guard';
import { AppointmentActionsService } from '../../../portal-appointments/services/appointment-actions.service';
import { LayoutService } from '../../../shared/services/layout.service';
import { MobileCheckInSessionService } from '../../../shared/services/mobile-check-in-session.service';
import { OrbiAppointmentCardNavigate } from '../../components/orbi-appointment-card/orbi-appointment-card.component';
import { OrbiFindABillFound } from '../../components/orbi-find-a-bill-card/orbi-find-a-bill-card.component';
import { OrbiFindLabSearch } from '../../components/orbi-find-lab-card/orbi-find-lab-card.component';
import { OrbiMessageInputComponent } from '../../components/orbi-message-input/orbi-message-input.component';
import { OrbiMessageItemComponent } from '../../components/orbi-message-item/orbi-message-item.component';
import { OrbiResultsCardNavigate } from '../../components/orbi-results-card/orbi-results-card.component';
import { OrbiAnnouncerService } from '../../services/orbi-announcer.service';
import { OrbiAppointmentScheduleNavigationService } from '../../services/orbi-appointment-schedule-navigation.service';
import { OrbiBillingNavigationService } from '../../services/orbi-billing-navigation.service';
import { OrbiFindLabHandoffService } from '../../services/orbi-find-lab-handoff.service';
import { OrbiTrackingService } from '../../services/orbi-tracking.service';
import {
  resolveOrbiAppointmentCardAction,
  resolveOrbiAppointmentCardVariant,
  resolveOrbiInsuranceState,
} from '../../utils/orbi-track-event.util';

/**
 * Whether one stored turn renders the Upcoming Appointment card — the only card
 * that reads the insurance-eligibility map (see `warmEligibility`).
 *
 * `isReturnType` normalizes before comparing, for the same reason
 * `OrbiMessageItemComponent.isAppointmentTurn` does: the mapper routes a payload to
 * `'appointment-card'` off a normalized return type, so a strict `===` here would
 * disagree with the decision that put the card on screen.
 */
function isAppointmentDetailsTurn(message: OrbiMessage): boolean {
  return (
    message.type === OrbiMessageType.Llm &&
    message.route === 'appointment-card' &&
    isReturnType(
      message.structuredData?.returnType,
      ORBI_RETURN_TYPE.AppointmentDetails
    )
  );
}

/**
 * Orbi conversation thread (PCSA-13160).
 *
 * The smart container that mounts inside the chat panel body: renders the
 * active conversation (`selectOrbiThreadMessages` — the stored turns, including
 * the "thinking" slot each outstanding send RESERVES for its own answer) as a
 * scrolling feed
 * of `patient-ui-orbi-message-item`s and hosts the message input. On submit it
 * derives the page context from the router URL (`matchOrbiRequestContext`) and
 * dispatches `sendMessage` — the existing effect (PCSA-13164) owns the session
 * id, the Split-flag gate, the transport, and the parse.
 *
 * The input stays fully USABLE while the agent is thinking (PCSA-13312, 08/14
 * Vibe): the user can type, dictate and send a second message, which the effect
 * queues (`concatMap`) so each answer arrives in its own bubble. Only a guardrail
 * lock disables it (PCSA-13170).
 *
 * Welcome section (PCSA-13159 / PCSA-13401): the "Hi, {name}" + "How can I help
 * you?" header is a PERSISTENT block at the top of the feed, not an empty state.
 * It was originally gated on an empty thread, which meant it was never seen at
 * all on the most common path — the entry bar sends the message AND opens the
 * panel, so a turn already existed by the time the thread first rendered
 * (PCSA-13401). It now hides only while a guardrail locks the chat. The helper
 * line under the subtitle keeps its own narrower rule (`newConversation$`: no
 * turns yet, empty input, no send in flight), so it comes and goes as the user
 * types and retires once the conversation starts (PCSA-13445).
 *
 * Conversation date (design review 08/20): a divider above the first turn,
 * showing the resumed conversation's own `createdAt` or today for a conversation
 * started here — see `conversationDate$`. Design review 08/21 gave it its own
 * wording ("Today, Aug 21, 2026"), so it no longer shares the history row's
 * format; the two still share the day resolution underneath, which is what stops a
 * conversation being dated twice over.
 *
 * Suggested prompts (PCSA-13159): the LAST thing in the feed, so they sit under
 * the newest turn and above the disclaimer exactly as the Figma canvas draws them
 * — and they come back after every answer rather than retiring with the empty
 * state (`showPrompts$`, AC 1.2/1.3/1.4). One instance stays mounted and is
 * toggled with `hidden` rather than `*ngIf`-ed in and out: the chip set is random,
 * and re-creating the component on every keystroke re-rolled it under the user.
 * The "How does this work?" chip is the exception and IS gated on an empty thread
 * (`[showHowItWorks]`, design review 08/19) — an explainer for a patient who has
 * not asked anything yet has no business sitting under a live answer.
 *
 * Mid-conversation the row is offered under EVERY answer except a guardrail's
 * (design review 08/20), and goes while the user is typing. The 08/19 rule that
 * additionally required the field to be focused is gone — see `showPrompts$`.
 *
 * Billing hand-off (PCSA-13416): a billing turn's answer also names a portal
 * page, so each answered turn is offered to `OrbiBillingNavigationService`, which
 * owns the ~1.5s pause, the panel close and the routing. Only turns answered while
 * this thread is mounted — i.e. while the panel is open — are handed off.
 *
 * Scroll behavior — parity with mobile `orbi_chat_content.dart`:
 *  - OPENING onto a conversation that already has turns (reopening the panel, or
 *    resuming one from history): land on the most recent message rather than the
 *    start of the conversation — deferred by a macrotask, once per thread, see
 *    `landOnLatest`.
 *  - While loading (the thinking turn is last): scroll to the BOTTOM so the
 *    thinking bubble is visible.
 *  - When a new agent (llm/error) turn arrives: align the TOP of that response
 *    into view (`scrollToLastResponse`) so the user reads it from the start —
 *    NOT the absolute bottom (mobile `_scrollToLastResponse`). The input also
 *    blurs so attention moves to the answer (mobile `_onMessagesChanged`).
 *  - A user turn just sent: scroll to bottom (keep the sent bubble in view).
 *  - An EARLIER question's answer landing while a later one is still outstanding
 *    (only reachable now that sends queue): hold the bottom, where the question
 *    the user is actually waiting on is — see `pendingSlots`.
 *  - The suggested-prompt row APPEARING — it is the last thing in the feed and it
 *    can appear without any turn changing (the user clears the field), so it lands
 *    below the fold half-drawn unless it is scrolled in: see `revealPrompts`. When
 *    a turn DID change in the same pass, the turn's scroll wins and the row is not
 *    chased: an answer must be read from its top, not skipped past to the chips
 *    below it (design review 08/20).
 * The floating "scroll to latest" button shows whenever the last bubble's
 * bottom extends below the visible feed area (mobile `_isLastBubbleBelowVisibleArea`).
 */
@Component({
  selector: 'patient-ui-orbi-thread',
  templateUrl: './orbi-thread.component.html',
  styleUrls: ['./orbi-thread.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiThreadComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  @ViewChild('feed') private feed?: ElementRef<HTMLElement>;
  /**
   * The suggested-prompt row's host, so the container can tell whether it is on
   * screen and how tall it is — see `revealPrompts`.
   */
  @ViewChild('prompts') private prompts?: ElementRef<HTMLElement>;
  @ViewChild(OrbiMessageInputComponent)
  private input?: OrbiMessageInputComponent;
  /**
   * The rendered message items, read as a typed model contract (each exposes
   * `message`) instead of querying `OrbiMessageItemComponent`'s presentation CSS
   * classes — renaming a class can no longer silently break scroll routing.
   */
  @ViewChildren(OrbiMessageItemComponent)
  private items?: QueryList<OrbiMessageItemComponent>;

  readonly messages$: Observable<OrbiMessage[]> = this.store.select(
    selectOrbiThreadMessages
  );
  /**
   * Whether ANY send is still awaiting the agent — a COUNT-backed flag, not the
   * request status (PCSA-13312): the user can now queue a second message while
   * the first is generating, and the status would read Success as soon as the
   * first answer landed.
   */
  readonly sendPending$: Observable<boolean> = this.store.select(
    selectOrbiSendPending
  );

  /** Whether the chat is locked by a hard guardrail (PCSA-13170). */
  readonly chatLocked$: Observable<boolean> = this.store.select(
    selectChatLockedForSession
  );

  /**
   * The welcome greeting line (PCSA-13159 Figma / PCSA-13401). Renders
   * "Hi, {firstName}" once the patient resolves and a bare "Hi" until then —
   * NOT "Hi," with a dangling comma, which is what a raw name interpolation
   * produced while the patient call was still in flight. Mobile takes the same
   * precaution (`hasFirstName` in `orbi_chat_content.dart`), and because the
   * greeting is now persistent rather than an empty state it is on screen far
   * more often, so the unresolved case is user-visible rather than a flicker.
   */
  readonly greetingTitle$: Observable<string> = this.store
    .select(selectPatient)
    .pipe(
      map((patient) => {
        const firstName = patient?.firstName?.trim();
        return firstName ? `Hi, ${firstName}` : 'Hi';
      })
    );

  /**
   * Whether this is still a NEW conversation — no real turn has been sent yet.
   *
   * Reads the RAW stored turns rather than `selectOrbiThreadMessages`, which
   * synthesizes loading/error bubbles — a transport failure must not read as
   * "the conversation started" if the reducer rolled the turn back.
   *
   * The greeting's title + subtitle deliberately do NOT use this (PCSA-13401):
   * they are a persistent header that survives the whole session.
   */
  readonly threadEmpty$: Observable<boolean> = this.store
    .select(selectOrbiMessages)
    .pipe(map((messages) => messages.length === 0));

  /**
   * The conversation's date, shown as a divider above its first turn (design
   * review 08/20: "add conversation date to chat"), or `''` when there is nothing
   * to date.
   *
   * Two sources, because the service gives the client exactly one timestamp and
   * only for a STORED conversation: a resumed one is dated by its own
   * `metadata.createdAt` (carried in on `resumeConversation` → `startedAt`), and
   * one started in this session is dated today. `formatOrbiChatDate` owns that
   * choice — and the UTC/local split behind it — so this thread and the history
   * row it was opened from cannot disagree about a conversation's date. They do
   * state it differently: the row is `MM/DD/YYYY` and this is design's long form
   * ("Today, Aug 21, 2026" / "Friday, Aug 14, 2026", design review 08/21), which
   * is why there are two formatters over one shared day resolution.
   *
   * Gated on `threadEmpty$`: the greeting stands alone on a conversation with no
   * turns, and dating a chat nobody has started yet would label the empty state.
   * `new Date()` is read per emission rather than at construction, so a panel left
   * open across midnight dates the next conversation correctly — and stops saying
   * "Today" about the one before it.
   */
  readonly conversationDate$: Observable<string> = combineLatest([
    this.store.select(selectOrbiConversationStartedAt),
    this.threadEmpty$,
  ]).pipe(
    map(([startedAt, threadEmpty]) =>
      threadEmpty ? '' : formatOrbiChatDate(startedAt, new Date())
    )
  );

  /**
   * Whether the input is currently empty (reported by the message input). Starts
   * true (the field is empty on mount). Held as a subject as well as a plain
   * field because `newConversation$` below has to recompute when it flips.
   */
  private readonly inputEmpty$ = new BehaviorSubject<boolean>(true);

  /** Whether the input is currently empty — the synchronous view of the above. */
  get inputEmpty(): boolean {
    return this.inputEmpty$.value;
  }

  /**
   * Whether the NEWEST turn is a guardrail response (design review 08/20: the
   * prompts come back after each response "except guardrail").
   *
   * A guardrail answer declines to help with what was asked — a row of chips
   * offering more things to ask reads as if the refusal had not happened. The HARD
   * guardrails (EMERGENCY / HARM) are covered by `chatLocked$` below as well, but
   * a soft one does not lock the chat, and that is the case this excludes.
   *
   * Reads the RAW stored turns: a synthesized loading/error bubble is not an
   * answer at all, and the flag lives on the stored turn.
   */
  private readonly lastTurnGuardrailed$: Observable<boolean> = this.store
    .select(selectOrbiMessages)
    .pipe(
      map(
        (messages) => messages[messages.length - 1]?.guardrailTriggered === true
      )
    );

  /**
   * Whether the welcome block should still show its NEW-conversation extra: the
   * helper line under "How can I help you?" (PCSA-13445). It is an entry ramp for
   * a patient who has not asked anything yet, so it retires once a turn exists,
   * while a send is in flight, or while the user is typing.
   *
   * The suggested prompts used to share this flag; they now have their own,
   * broader one (`showPrompts$`) because the AC wants them BACK after every
   * answer. That does not reopen PCSA-13445 — which was the helper copy outliving
   * the prompts, still offering "ask about your test results" over an active
   * thread — because this flag is the strictly narrower of the two: the helper
   * line can never be on screen without the prompts.
   *
   * The greeting's title + subtitle are NOT gated on it (PCSA-13401 — they
   * persist).
   */
  readonly newConversation$: Observable<boolean> = combineLatest([
    this.threadEmpty$,
    this.inputEmpty$,
    this.sendPending$,
  ]).pipe(
    map(
      ([threadEmpty, inputEmpty, sendPending]) =>
        threadEmpty && inputEmpty && !sendPending
    )
  );

  /**
   * Whether the suggested prompts are on screen (PCSA-13159 AC 1 + AC 2).
   *
   * They stand above the input whenever the user COULD ask something: after every
   * response, on a new conversation, and the moment a half-typed question is
   * cleared. The hide rules are AC 2's own — while the user is typing
   * (`inputEmpty`), while a submitted message is still loading (`sendPending`),
   * and while a guardrail lock has disabled the input (`chatLocked`) — plus the
   * 08/20 exception for a guardrail RESPONSE (`lastTurnGuardrailed$`).
   *
   * This is not gated on the field's focus. It was, briefly (design review 08/19:
   * "when Ask a question is active then only show this"), which the 08/20 review
   * reversed in favour of the story's own AC 1.4 — the prompts render above the
   * input after each response, per Shikha's mock. The focus rule also made AC 1.4
   * nearly unreachable in practice, since a landing response BLURS the field
   * (`routeScroll`, mobile parity), so the chips it promises "when the agent
   * response is returned" never appeared until the user clicked back into the
   * input.
   */
  readonly showPrompts$: Observable<boolean> = combineLatest([
    this.inputEmpty$,
    this.sendPending$,
    this.chatLocked$,
    this.lastTurnGuardrailed$,
  ]).pipe(
    map(
      ([inputEmpty, sendPending, chatLocked, lastTurnGuardrailed]) =>
        inputEmpty && !sendPending && !chatLocked && !lastTurnGuardrailed
    )
  );

  /**
   * The portal's own insurance-eligibility answers, handed to every rendered turn
   * so an appointment card offers the insurance action the Appointments page
   * offers (PCSA-13543).
   *
   * The flag is the `/me/insurances/search` result the patient effects stitch onto
   * the store's appointment rows — it is not a scheduling field, so no agent
   * payload can carry it, and without this the card had nothing to branch on and
   * rendered no insurance CTA while the Appointments page rendered "Review
   * Insurance" for the same appointment.
   *
   * The panel warms the slice itself when it has to — see `warmEligibility`. It
   * floats over every page in the portal, so it deliberately does not treat a chat
   * turn as a reason to refetch the appointment list plus an eligibility search per
   * row; but the slice is only filled by the Appointments feature routes, and a
   * patient who opens the panel from the dashboard, results or invoices has never
   * been to one. That was the common path, and on it the map stayed empty and every
   * card fell back to what its payload alone could state — so the parity this
   * exists to deliver (PCSA-13543) was missing exactly where the panel is most used
   * (PR review 08/25).
   *
   * A cold slice is still SAFE rather than wrong: `resolveOrbiInsuranceTask`
   * degrades "Review Insurance" to "Update Insurance" rather than dropping the CTA,
   * which is why this was shippable without the warm-up and why the warm-up can
   * afford to be as narrow as it is.
   *
   * Held as a plain field rather than piped into each turn: one subscription for
   * the whole feed instead of one per rendered message, and every card then shares
   * the same map identity so their OnPush input checks short-circuit.
   */
  insuranceEligibility: OrbiInsuranceEligibilityMap = {};

  /**
   * The appointments this browser session has already checked into, handed to every
   * rendered turn so their cards draw the disabled "Checked In" pill (PCSA-13577).
   *
   * Read ONCE per panel lifetime, in `ngOnInit`. Checking in cannot happen while
   * this component is alive — the check-in action navigates to
   * `/portal/appointments/check-in` and closes the panel, which destroys it — so
   * the next read is the next open, and it is already the fresh one. Held as a
   * plain field for the same reason `insuranceEligibility` is: one array identity
   * for the whole feed, so every card's OnPush input check short-circuits.
   */
  checkedInConfirmationNumbers: readonly string[] = [];

  /**
   * The active conversation's id. `startNewConversation` mints a new one;
   * resuming from history adopts the stored conversation's.
   *
   * Private: nothing binds it any more. It used to be the prompts' rotation key on
   * its own, and is now one half of `promptsKey$`.
   */
  private readonly sessionId$: Observable<string> =
    this.store.select(selectOrbiSessionId);

  /**
   * What the suggested prompts re-roll their subset on: the conversation, plus how
   * many answers it has received.
   *
   * The answer count is the fix for "the prompts aren't rotating after a response
   * arrives — they rotate when I close and reopen the conversation" (design review
   * 08/28). The key was the session id alone, which is exactly one value per
   * conversation, so reopening the panel was the only thing that ever changed it.
   *
   * Counting LANDED ANSWERS rather than turns is what makes this safe to feed a
   * live component. The chips are on screen for the whole time the user is reading
   * an answer and deciding what to ask next, and they must not move under a hand
   * that is reaching for one — so the key has to be a value that changes only
   * while they are hidden. `sendPending` hides them for the entire round trip
   * (`showPrompts$`), and the count changes at the far end of it, so the swap is
   * always off screen. A raw `messages.length` would additionally tick on the
   * user's OWN send and on a rolled-back failure; both of those are moments when
   * the row is coming back unchanged, and rotating then would spend a fresh set on
   * a question that never got answered.
   *
   * Reads the RAW stored turns, like `threadEmpty$`: the synthesized loading and
   * error bubbles in `selectOrbiThreadMessages` are not answers, and a thinking
   * bubble in particular would rotate the set at the START of the wait.
   */
  readonly promptsKey$: Observable<string> = combineLatest([
    this.sessionId$,
    this.store
      .select(selectOrbiMessages)
      .pipe(
        map(
          (messages) =>
            messages.filter((message) => message.type === OrbiMessageType.Llm)
              .length
        )
      ),
  ]).pipe(
    map(([sessionId, answers]) => `${sessionId}:${answers}`),
    // One emission per actual rotation. The child's input check would swallow a
    // repeat anyway (the key is a string, compared by value), but every emission
    // costs the async pipe a change-detection pass over the panel.
    distinctUntilChanged()
  );

  /** Whether the "scroll to latest" affordance is shown (user scrolled away). */
  showScrollToBottom = false;

  /** Jitter threshold: last bubble must extend >40px below the fold to show FAB. */
  private static readonly BELOW_FOLD_PX = 40;

  /** Tracks the rendered turn count to detect growth. */
  private renderedCount = 0;
  /** The type of the last rendered turn, to route the scroll on change. */
  private lastTurnType: OrbiMessageType | null = null;
  /**
   * How many answer slots are still outstanding (thinking bubbles on screen).
   *
   * The count and the last turn's type together miss ONE mutation, and with a
   * queue it is a routine one: two messages outstanding, and the FIRST answer
   * lands. It replaces its own slot in the middle of the thread
   * (`fillSlot`/`oldestPendingIndex` in the reducer), so the turn count does not
   * change and the last turn is still the second question's thinking bubble —
   * nothing for `grew` or `lastTypeChanged` to see, while content the height of a
   * whole response has just been inserted ABOVE the user, pushing what they were
   * watching off the bottom. A slot being consumed is exactly what that mutation
   * is, so it is what gets tracked.
   */
  private pendingSlots = 0;
  /** Whether the chip row was on screen at the last check — see `revealPrompts`. */
  private promptsShown = false;
  /**
   * Whether the opening position has been claimed yet — see `landOnLatest`. Set on
   * the first render that HAS turns, which is the mount for a conversation the
   * panel reopened onto and the load for one resumed from history.
   */
  private landed = false;
  /**
   * Whether the user was following at the bottom, captured from ACTUAL scroll
   * events (`onFeedScroll`) — i.e. BEFORE a new turn inflates `scrollHeight`.
   * Measuring in `ngAfterViewChecked` instead would read a stale distance (the
   * response already grew the feed) and misclassify a following user as
   * scrolled-up. Starts true so the first turn scrolls into view.
   */
  private wasNearBottom = true;

  private pendingGuidanceFocus = false;

  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly actions$: Actions,
    private readonly announcer: OrbiAnnouncerService,
    private readonly layoutService: LayoutService,
    private readonly appointmentActions: AppointmentActionsService,
    private readonly billingNavigation: OrbiBillingNavigationService,
    private readonly scheduleNavigation: OrbiAppointmentScheduleNavigationService,
    private readonly findLabHandoff: OrbiFindLabHandoffService,
    private readonly checkInSession: MobileCheckInSessionService,
    private readonly tracking: OrbiTrackingService
  ) {}

  ngOnInit(): void {
    // See `checkedInConfirmationNumbers` — one read per panel open is enough.
    this.checkedInConfirmationNumbers = this.checkInSession.codes;

    // A billing answer names a portal page, and the answer's text is only half of
    // it — the hand-off is the other half (PCSA-13416). Started from HERE, not
    // from an effect, because this component exists only while the panel is open:
    // a user who closed the chat and moved on is not yanked onto a billing page by
    // an answer that lands afterwards. The service owns the delay from here on,
    // since completing the hand-off closes the panel and destroys this component.
    this.actions$
      .pipe(ofType(sendMessageSuccess), takeUntil(this.destroyed))
      .subscribe(({ message }) => {
        this.billingNavigation.handleAnsweredTurn(message);
        this.scheduleNavigation.handleAnsweredTurn(message);
      });

    this.sendPending$
      .pipe(
        distinctUntilChanged(),
        filter((pending) => pending),
        takeUntil(this.destroyed)
      )
      .subscribe(() => this.announcer.announce('Ask AI is thinking.'));

    this.actions$
      .pipe(ofType(sendMessageSuccess), takeUntil(this.destroyed))
      .subscribe(({ message }) => {
        this.tracking.trackResponseRendered(message);
        this.announceAnsweredTurn(message);
      });

    this.actions$
      .pipe(ofType(sendMessageFailure), takeUntil(this.destroyed))
      .subscribe(() => this.tracking.trackErrorRendered());

    // Insurance eligibility for the appointment cards (see `insuranceEligibility`).
    // `markForCheck` because this container is OnPush and the slice can be filled
    // by the appointment effects while the panel sits open — the card's action row
    // would otherwise keep the answer it was first rendered with.
    this.store
      .select(selectPatientAppointments)
      .pipe(takeUntil(this.destroyed))
      .subscribe((appointments) => {
        this.insuranceEligibility = buildOrbiInsuranceEligibility(appointments);
        this.cdr.markForCheck();
      });

    this.warmEligibility();
  }

  /**
   * Fill the eligibility slice ONCE, the first time a turn actually renders an
   * appointment card (PR review 08/25 — see `insuranceEligibility`).
   *
   * `loadPatientAppointments` is what triggers the `/me/insurances/search` fan-out:
   * the patient effects dispatch `checkInsuranceEligibility({ appointments })` off
   * its success, so it is the only way to warm the flag the cards read. It is
   * dispatched from the four Appointments feature routes and nowhere else, so
   * without this the panel could only ever show the parity fix to a patient who had
   * already been to one of them.
   *
   * Narrow on purpose, in three ways — the panel outlives every page it floats over
   * and must not turn a conversation into a stream of appointment fetches:
   *  - It waits for an AppointmentDetails turn. No appointment card on screen means
   *    nothing reads the map, so nothing justifies the call — a patient asking about
   *    a bill never triggers it.
   *  - `NotSent` only. A slice already loaded is not refetched, a load in flight is
   *    not raced, and a load that FAILED is not retried in a loop by every
   *    subsequent turn.
   *  - `take(1)`. One warm-up per panel lifetime, whatever the conversation does
   *    afterwards. The subscription in `ngOnInit` picks the result up when it lands
   *    and re-renders the cards already on screen, so the CTA corrects itself
   *    in place rather than needing the turn to arrive after the data.
   *
   * The predicate repeats `OrbiMessageItemComponent`'s own appointment-turn test
   * rather than reaching into the rendered children: `@ViewChildren` are not
   * resolved yet in `ngOnInit`, and the message list is the thing that decides
   * whether a card exists at all.
   */
  private warmEligibility(): void {
    this.messages$
      .pipe(
        filter((messages) => messages.some(isAppointmentDetailsTurn)),
        take(1),
        switchMap(() =>
          this.store
            .select(selectPatientAppointmentsRequestStatus)
            .pipe(take(1))
        ),
        filter((status) => status === RequestStatus.NotSent),
        takeUntil(this.destroyed)
      )
      .subscribe(() => this.store.dispatch(loadPatientAppointments()));
  }

  private announceAnsweredTurn(message: OrbiMessage): void {
    if (
      orbiMessageLocksChat(message.guardrailTriggered, message.guardrailType)
    ) {
      this.announceLock(message);
      return;
    }
    this.announcer.announce('Response ready.');
  }

  private announceLock(message: OrbiMessage): void {
    this.announcer.alert(message.text);
    this.pendingGuidanceFocus = this.input?.focused === true;
  }

  private rescueFocusToGuidance(): boolean {
    const guidance = this.items
      ?.filter((item) => item.isGuardrailLockTurn)
      .pop();
    return guidance?.focusGuidance() === true;
  }

  ngOnDestroy(): void {
    this.announcer.clear();
    // Billing deliberately does not cancel (see `OrbiBillingNavigationService`).
    this.scheduleNavigation.cancel();
    this.destroyed.next();
    this.destroyed.complete();
  }

  onSend(text: string): void {
    this.tracking.trackMessageSent(this.input?.inputMethod ?? 'typed');
    // Build the request context LIVE at send time (PCSA-13222): the nav label(s)
    // for the current route plus, on the results detail page, the accession the
    // portal already loaded (read as a one-shot store snapshot — no new fetch).
    this.store
      .select(selectResultDetail)
      .pipe(take(1))
      .subscribe((resultDetail) =>
        this.store.dispatch(
          sendMessage({
            userMessage: text,
            requestContext: buildOrbiRequestContext(this.router.url, {
              resultDetail,
            }),
          })
        )
      );
  }

  /**
   * Resend the last failed message (PCSA-13172 retry). The `retryLastMessage$`
   * effect (PCSA-13164) re-dispatches `sendMessage` with the stashed text +
   * context; the synthesized error turn is replaced by a fresh loading turn.
   */
  onRetry(): void {
    this.store.dispatch(retryLastMessage());
  }

  /**
   * A navigation intent from a rendered Results card (PCSA-13224). Mirrors the
   * mobile flow (`orbi_results_card.dart` → `setOverlayOpen(false)` before
   * navigating): CLOSE the Orbi panel first so the user lands on the results
   * page unobstructed, THEN route:
   *  - "View Details" (single frame) / "View ›" (a multi frame row) → the
   *    accession detail
   *    (`/portal/results/detail/:id`, with the `pid` query param when known —
   *    same shape as `portal-trends-details` and `portal-results-detail`).
   *  - "View All Results" → the results list (`/portal/results/list`).
   * A view-details intent with no accession (defensive) falls back to the list.
   */
  onCardNavigate(event: OrbiResultsCardNavigate): void {
    this.tracking.trackCardAction({
      card_type: 'results',
      action:
        event.kind === 'view-details' ? 'view_result' : 'view_all_results',
    });
    this.layoutService.setOrbiPanelOpen(false);

    if (event.kind === 'view-details' && event.accession) {
      const { id, pid } = event.accession;
      this.router.navigate(
        ['/portal/results/detail/', id],
        pid != null ? { queryParams: { pid } } : {}
      );
      return;
    }

    this.router.navigate(['/portal/results/list']);
  }

  /**
   * An action activated on an Upcoming Appointment card (PCSA-13397 AC 7). Same
   * shape as the Results-card flow: CLOSE the panel first, then route to the
   * portal destination the action owns:
   *  - Appointment Details → `/portal/appointments/detail/:confirmationCode`
   *  - Mobile Check-in     → `/portal/appointments/check-in`
   *  - Provide/Update Insurance → `/portal/appointments/insurance/provider`
   *  - Review Insurance    → `/portal/appointments/insurance-review/:confirmationCode`
   *
   * Details and Review Insurance route by URL param, so navigating is the whole
   * job. Check-in and the insurance ENTRY flow do not: each reads its appointment
   * out of `sessionStorage` on init and bounces back to `/portal/appointments`
   * when the key is missing, so they are delegated to `AppointmentActionsService`,
   * which seeds that state from the find API (PR 3170 review — these two CTAs
   * were dead ends, landing the user on the appointments list). The panel is
   * already closed by then, and both seeds are keyed off the confirmation number
   * alone: nothing else on an `OrbiAppointment` matches what those pages read.
   *
   * All four confirmation-keyed destinations fall back to the appointments
   * dashboard if the row somehow arrived without a confirmation number — the
   * actions are payload-gated on it, so this is defensive only, never a dead-end
   * route.
   */
  onAppointmentNavigate(event: OrbiAppointmentCardNavigate): void {
    const insuranceState = resolveOrbiInsuranceState(event.appointment);
    this.tracking.trackCardAction({
      card_type: 'appointment',
      action: resolveOrbiAppointmentCardAction(event.action),
      card_variant: resolveOrbiAppointmentCardVariant(event.appointment),
      ...(insuranceState && { insurance_state: insuranceState }),
    });
    this.layoutService.setOrbiPanelOpen(false);
    const confirmationNumber = event.appointment.confirmationNumber;

    switch (event.action) {
      case ORBI_APPOINTMENT_ACTION.details:
        this.router.navigate(
          confirmationNumber
            ? ['/portal/appointments/detail', confirmationNumber]
            : ['/portal/appointments']
        );
        return;
      case ORBI_APPOINTMENT_ACTION.checkIn:
        this.openSeededFlow(confirmationNumber, (code) =>
          this.appointmentActions.navigateToCheckIn(code)
        );
        return;
      case ORBI_APPOINTMENT_ACTION.reviewInsurance:
        this.router.navigate(
          confirmationNumber
            ? ['/portal/appointments/insurance-review', confirmationNumber]
            : ['/portal/appointments']
        );
        return;
      default:
        // Provide / Update Insurance share the insurance-provider entry point,
        // matching every other portal navigation to that flow.
        this.openSeededFlow(confirmationNumber, (code) =>
          this.appointmentActions.navigateToInsuranceByConfirmation(code)
        );
        return;
    }
  }

  /**
   * Open one of the two session-seeded appointment flows.
   *
   * Both need a confirmation number to look the appointment up, and both can fail
   * on that lookup. Either way the user goes to the appointments list rather than
   * to a page that would immediately redirect them there itself — same
   * destination, but reached without a flash of the wrong screen.
   */
  private openSeededFlow(
    confirmationNumber: string | undefined,
    seed: (confirmationCode: string) => Promise<void>
  ): void {
    if (!confirmationNumber) {
      this.router.navigate(['/portal/appointments']);
      return;
    }
    seed(confirmationNumber).catch(() =>
      this.router.navigate(['/portal/appointments'])
    );
  }

  /**
   * "See All" on an Upcoming Appointment card set (or the Visit History card split
   * from it) → the Appointments page at every width, with the panel closed first.
   * Not the upcoming sub-page: that is a table scoped to upcoming only.
   *
   * Both carry `APPOINTMENTS_IN_APP_NAVIGATION`: those two routes are guarded, and
   * the guard used to admit a navigation only when the PREVIOUS url was already
   * under /portal/appointments. The panel floats over whatever page the user is on,
   * so "See All" bounced to the appointments dashboard from everywhere else —
   * PCSA-13518 / PCSA-13522, which read as the CTA going to the wrong page. The
   * flag states the intent the referrer could not (see the guard for why that is
   * the same line, not a weaker one). Harmless on the dashboard, which is
   * unguarded.
   */
  onAppointmentsSeeAll(): void {
    this.tracking.trackCardAction({
      card_type: 'appointment',
      action: 'see_all',
    });
    this.layoutService.setOrbiPanelOpen(false);
    this.router.navigate(['/portal/appointments'], {
      state: { [APPOINTMENTS_IN_APP_NAVIGATION]: true },
    });
  }

  /**
   * "See All" on a standalone Visit History card → the Appointments page, at
   * every width, with the panel closed first. Every See All in Ask AI lands on
   * `/portal/appointments` (PCSA-14111): the page lists upcoming and past
   * together, where the history sub-page is a past-only table (and absent below
   * md). Same `APPOINTMENTS_IN_APP_NAVIGATION` flag as `onAppointmentsSeeAll`.
   */
  onVisitHistorySeeAll(): void {
    this.tracking.trackCardAction({
      card_type: 'rate_visit',
      action: 'see_all',
    });
    this.layoutService.setOrbiPanelOpen(false);
    this.router.navigate(['/portal/appointments'], {
      state: { [APPOINTMENTS_IN_APP_NAVIGATION]: true },
    });
  }

  /**
   * "Rate Visit" on a Visit History card. The card's control is a real external
   * link (it opens the Rate Your Lab Visit site in a new tab), so there is no
   * routing to do here and the panel deliberately stays OPEN — the user is sent to
   * another tab and comes back to the conversation they left. This exists so the
   * intent still surfaces to the thread for analytics parity with the other cards.
   */
  onRateVisit(): void {
    this.tracking.trackCardAction({
      card_type: 'rate_visit',
      action: 'rate_visit',
    });
  }

  onAddToWallet(wallet: OrbiAgentWalletType): void {
    this.tracking.trackCardAction({
      card_type: 'patient_pass',
      action: 'add_to_wallet',
      wallet_type: wallet,
    });
  }

  /**
   * A search submitted from a Make an Appointment card (PCSA-13241 AC 6).
   */
  onFindLabSearch(event: OrbiFindLabSearch): void {
    this.tracking.trackCardAction({
      card_type: 'find_a_lab',
      action: 'find_lab_location',
      service: event.serviceId,
    });
    this.findLabHandoff.openResults(event);
  }

  /**
   * A bill located from a Find a Bill card (PCSA-13419 AC 8). Same shape as every
   * other card handoff — CLOSE the panel first, then route — and the destinations
   * plus the state they need are the Billing page's own
   * (`PortalDashboardInvoicesSearch`), not a second implementation of them:
   *  - `open` + Pay My Bill      → seed the payment list + wallet, then
   *                                `/portal/invoices/portalPayment` (checkout).
   *  - `open` + Update Insurance → the same seeding, then
   *                                `/invoices/insurance?navigatedFrom=RU`.
   *  - `pending`                 → the bill already has a payment in flight, so
   *                                BOTH actions go to the confirmation page
   *                                instead, exactly as the Billing page does;
   *                                offering checkout again would risk a second
   *                                payment on one invoice.
   *
   * The wallet fetch is what makes the checkout page open with saved payment
   * methods rather than an empty form, and `internalNavigation` is what tells the
   * confirmation page it was reached from inside the app; both are copied because
   * omitting either would give the chat entry point a subtly different flow from
   * the Billing page's.
   */
  onFindABillFound(event: OrbiFindABillFound): void {
    this.tracking.trackCardAction({
      card_type: 'find_a_bill',
      action:
        event.action === 'updateInsurance' ? 'update_insurance' : 'pay_my_bill',
    });
    this.layoutService.setOrbiPanelOpen(false);

    if (event.status === 'pending') {
      this.store.dispatch(
        addPayment({
          payload: {
            invoiceNumber: event.invoice.number,
            paymentAmount: event.invoice.invoiceAmount,
            paymentDue: event.invoice.invoiceAmount,
            specimenNumber: event.invoice.specimenNumber,
            dunningCode: event.invoice.dunningCycleCode ?? '',
            orderingPhyAccountNumber: event.invoice.orderingPhyAccountNumber,
          },
        })
      );
      sessionStorage.setItem('internalNavigation', 'true');
      this.router.navigate(['/portal/invoices/portalConfirmation']);
      return;
    }

    this.store.dispatch(
      addSelectedPaymentList({ payload: Array.of(event.invoice) })
    );
    this.store.dispatch(getPatientWallet());

    if (event.action === 'updateInsurance') {
      // The bill in the URL as well as in the store: the selection above is in
      // memory, so a refresh on the Insurance page empties it and that page ejects
      // to the Billing list. It rebuilds from this param instead (PCSA-13312).
      this.router.navigate(['/invoices/insurance'], {
        queryParams: {
          navigatedFrom: 'RU',
          invoices: event.invoice.number,
        },
      });
      return;
    }

    this.router.navigate(['/portal/invoices/portalPayment']);
  }

  /**
   * The "sign out" link in the locked-session tooltip (PCSA-13170). A locking
   * guardrail (EMERGENCY / HARM) holds the AI Agent for the whole login session
   * — logging out (and back in) is the only recovery, since the lock is cleared
   * on `logOutSuccess`. Dispatches the app logout with the same `/landing`
   * redirect the portal uses elsewhere.
   */
  onSignOut(): void {
    this.store.dispatch(logOut({ redirect: '/landing' }));
  }

  /**
   * A suggested prompt was chosen (PCSA-13159): pre-fill the input, no auto-send
   * — the user keeps control (AC 8).
   */
  onPromptSelected(fullPrompt: string): void {
    this.input?.setText(fullPrompt);
  }

  /**
   * The distinct "How does this work?" chip was chosen (PCSA-13159 AC 6): append
   * the explainer as a real conversation TURN — the chip's label as the user's
   * message, the approved copy as the agent's answer (design review 08/11, the
   * Figma "About Ask AI" frame). The store composes the pair from constants; no
   * request is made.
   *
   * The chip itself retires once the turn lands (design review 08/19): it is
   * offered on a NEW conversation only — `[showHowItWorks]` is bound to
   * `threadEmpty$` — so pressing it is also what removes it, since the explainer
   * pair is a real turn. The real prompts stay and come back after every
   * answer (see `showPrompts$`).
   */
  onHowItWorks(): void {
    this.store.dispatch(explainHowItWorks());
  }

  /**
   * The input reported its empty-state (gates the suggested prompts via
   * `showPrompts$` and the helper line via `newConversation$`). `markForCheck`
   * stays: the async pipe schedules its own check when a derived flag changes, but
   * this can be called from the input's own change detection pass, and
   * `newConversation$` does NOT change while the thread already has turns — the
   * template still needs to settle either way.
   */
  onInputEmptyChange(empty: boolean): void {
    this.inputEmpty$.next(empty);
    this.cdr.markForCheck();
  }

  /** trackBy for the message feed (index — turns are append-only). */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Places focus in the message input (PCSA-13169 AC 7d): called when a
   * conversation is resumed from history so the user can immediately continue it.
   */
  focusInput(): void {
    this.input?.focus();
  }

  ngAfterViewChecked(): void {
    const el = this.feed?.nativeElement;
    if (!el) {
      return;
    }
    const lastItem = this.items?.last;
    const count = this.items?.length ?? 0;
    const lastType = lastItem?.message?.type ?? null;
    const pendingSlots = this.items
      ? this.items.filter(
          (item) => item.message?.type === OrbiMessageType.Loading
        ).length
      : 0;

    const grew = count > this.renderedCount;
    // The last turn's own slot being filled: the answer to the newest question.
    const lastTypeChanged = lastType !== this.lastTurnType;
    // An EARLIER question's slot being filled while a later one is still
    // outstanding — invisible to both checks above (see `pendingSlots`).
    const earlierSlotFilled =
      !grew && !lastTypeChanged && pendingSlots < this.pendingSlots;
    const turnChanged = grew || lastTypeChanged || earlierSlotFilled;

    if (turnChanged) {
      this.renderedCount = count;
      this.lastTurnType = lastType;
      this.pendingSlots = pendingSlots;
    }

    if (lastItem && turnChanged) {
      const lastEl = lastItem.hostElement;
      // Consume the near-bottom state captured from the last real scroll event
      // (pre-mutation), NOT a post-render measurement — see `wasNearBottom`.
      if (earlierSlotFilled) {
        // Hold the bottom of the thread, where the still-outstanding question and
        // its thinking bubble are — but only for a user who was actually there.
        // NOT `routeScroll`: the answer that just landed is not the turn the user
        // is waiting on, so aligning its top into view would drag them backwards
        // up the thread, and blurring the input on it would be wrong for the same
        // reason. It is also why this cannot fall into the `else` branch's
        // unconditional jump to the bottom — nobody just acted here, so a reader
        // who had scrolled up keeps their place and gets the FAB instead (AC 10).
        if (this.wasNearBottom) {
          el.scrollTop = el.scrollHeight;
        }
      } else {
        this.routeScroll(el, lastEl, lastType, this.wasNearBottom);
      }
    }

    // The chip row can come and go without any turn changing (the user clears a
    // half-typed question), so nothing above sees it and it needs its own reveal.
    // It runs LAST and is suppressed when a turn changed in the same pass: the row
    // now stands under every response (design review 08/20), and it appears in the
    // very pass the answer does — chasing it would overwrite the answer's
    // top-alignment and land the user at the END of what they are meant to read.
    // Called unconditionally so the appearance is recorded either way.
    this.revealPrompts(el, !turnChanged);

    if (this.pendingGuidanceFocus) {
      this.pendingGuidanceFocus = false;
      this.rescueFocusToGuidance();
    }

    // Claim the opening position ONCE, on the first render that has turns — see
    // `landOnLatest` for why it cannot be done from inside this pass.
    if (!this.landed && lastItem) {
      this.landed = true;
      timer(0)
        .pipe(takeUntil(this.destroyed))
        .subscribe(() => this.landOnLatest());
    }

    this.syncScrollButton(el);
  }

  /**
   * Feed scroll handler (template). Fires on ACTUAL user scrolling — before any
   * new turn inflates the feed — so this is where we record whether the user is
   * following at the bottom (consumed later in `ngAfterViewChecked`). Also
   * toggles the scroll-to-latest button.
   */
  onFeedScroll(): void {
    const el = this.feed?.nativeElement;
    if (el) {
      this.wasNearBottom = this.isNearBottom(el);
      this.syncScrollButton(el);
    }
  }

  /** Jump to the most recent message / end of the latest response (AC 11). */
  scrollToBottom(): void {
    const el = this.feed?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
      this.syncScrollButton(el);
    }
  }

  // --- Scroll routing (mobile parity) ---------------------------------------

  /**
   * Bring the whole chip row into view when it appears (QA, 08/19: "#4 gets cut
   * off and the last 2 prompts aren't visible until I scroll").
   *
   * The row is the LAST child of the feed and it can appear without any turn
   * changing — the user clears a half-typed question — so nothing in
   * `ngAfterViewChecked`'s routing below reacts to it and the feed keeps the offset
   * it already had. A stack of chips can be taller than the space left under the
   * newest answer, so the row extends past the fold with no scroll affordance
   * pointing at it (the FAB tracks the last BUBBLE, not the chips) — at six chips
   * the fourth was sliced in half and the last two were off screen entirely.
   *
   * The set is three now (design review 08/21), so on a roomy panel the row often
   * fits and this is a no-op — the "already fully visible" guard below is what
   * makes that free. It still earns its place on a short viewport, at a large
   * browser text size, and behind a long answer, which are the cases where three
   * chips clear the fold no better than six did.
   *
   * Only on the false -> true edge, only when no TURN changed in the same pass
   * (`allowScroll` — that scroll wins, see the caller), and only for a user who was
   * at the bottom anyway. The chips are an offer, never the point of the screen: a
   * reader who has scrolled up into the conversation and clears the field keeps
   * their place, exactly as a new answer would leave it (AC 10). And
   * nothing moves when the row is already fully visible, which is the whole of the
   * empty-state frame and of any short conversation.
   *
   * Scrolls to the feed's end rather than by the clipped amount, because the row IS
   * the end — the only thing under it is the feed's own bottom padding.
   */
  private revealPrompts(feed: HTMLElement, allowScroll: boolean): void {
    const row = this.prompts?.nativeElement;
    if (!row) {
      return;
    }
    const shown = !row.hidden;
    if (shown === this.promptsShown) {
      return;
    }
    // Recorded even when the scroll is suppressed, so a later pass does not mistake
    // an old appearance for a new one.
    this.promptsShown = shown;
    if (!allowScroll || !shown || !this.wasNearBottom) {
      return;
    }
    // Same measurement idiom as `isLastBubbleBelowFold`: an offset inside the
    // feed's content, against the bottom edge of what is visible of it.
    const rowBottom = row.offsetTop - feed.offsetTop + row.offsetHeight;
    if (rowBottom > feed.scrollTop + feed.clientHeight) {
      feed.scrollTop = feed.scrollHeight;
    }
  }

  /**
   * Put the feed on the NEWEST turn as the thread's opening position (design review
   * 08/20: "land the user on the most recent message, not the top, when reopening an
   * active conversation").
   *
   * Reopening the panel mounts a FRESH thread (the layout only instantiates it while
   * the panel is open) over a conversation that is already many turns long, so every
   * bubble arrives in one render pass. Measuring inside that pass reads boxes the
   * panel has not finished laying out, the feed keeps whatever offset it was given,
   * and nothing afterwards corrects it — no turn changes again, so the routing above
   * never fires a second time. What the user sees is the START of the conversation.
   *
   * A macrotask later the browser has laid the feed out, which is why this is
   * deferred rather than done in `ngAfterViewChecked`. It runs ONCE per thread (see
   * `landed`) so it can never fight the live routing: from the next turn on, the
   * ordinary rules — including "a reader who scrolled up keeps their place" — own the
   * feed.
   *
   * Aligns exactly as a live answer does (`routeScroll`): the TOP of the last
   * response, so the newest answer is read from its start and not its end, and the
   * bottom for a question or a thinking bubble. It does NOT blur the input the way
   * `routeScroll` does — resuming a conversation from history deliberately puts the
   * caret in the field (PCSA-13169 AC 7d), and this lands right after it.
   */
  private landOnLatest(): void {
    const feed = this.feed?.nativeElement;
    const last = this.items?.last;
    if (!feed || !last) {
      return;
    }
    const type = last.message?.type ?? null;
    if (type === OrbiMessageType.Llm || type === OrbiMessageType.Error) {
      feed.scrollTop = Math.max(0, last.hostElement.offsetTop - feed.offsetTop);
    } else {
      feed.scrollTop = feed.scrollHeight;
    }
    // The browser fires a scroll event for this, which would record the same thing;
    // recorded here as well so the following state is right even where it does not
    // (and before any turn can arrive).
    this.wasNearBottom = this.isNearBottom(feed);
    this.syncScrollButton(feed);
  }

  private routeScroll(
    feed: HTMLElement,
    last: HTMLElement | undefined,
    lastType: OrbiMessageType | null,
    wasNearBottom: boolean
  ): void {
    if (
      lastType === OrbiMessageType.Llm ||
      lastType === OrbiMessageType.Error
    ) {
      // New agent response. If the user had scrolled UP to read history, respect
      // that — leave their position and let the scroll-to-latest button surface
      // (AC 10). Only when they were following at the bottom do we align the
      // response TOP into view so they read it from the start (mobile
      // `_scrollToLastResponse`) and blur the input (mobile unfocus).
      //
      // The blur is skipped when the field has text in it (PCSA-13312): now that
      // a second message can be typed while the first is still generating, the
      // first answer can land mid-sentence, and blurring would drop the caret out
      // of a half-written question — closing the keyboard on mobile web. Mobile's
      // unfocus predates a typable loading state; an EMPTY field still blurs, so
      // attention moves to the answer exactly as before.
      if (wasNearBottom && last) {
        feed.scrollTop = Math.max(0, last.offsetTop - feed.offsetTop);
        if (this.inputEmpty) {
          this.input?.blur();
        }
      }
    } else {
      // Loading turn or a just-sent user turn: keep the bottom in view.
      feed.scrollTop = feed.scrollHeight;
    }
  }

  /**
   * Whether the feed is scrolled at/near its bottom — i.e. the user is following
   * the conversation rather than reading back. Uses raw scroll distance (not the
   * last-bubble geometry) so it reflects the user's position independent of which
   * turn is currently last; read pre-mutation to capture their intent.
   */
  private isNearBottom(feed: HTMLElement): boolean {
    const distance = feed.scrollHeight - feed.scrollTop - feed.clientHeight;
    return distance <= OrbiThreadComponent.BELOW_FOLD_PX;
  }

  private syncScrollButton(feed: HTMLElement): void {
    const show = this.isLastBubbleBelowFold(feed);
    if (show !== this.showScrollToBottom) {
      this.showScrollToBottom = show;
      this.cdr.markForCheck();
    }
  }

  /**
   * Mobile parity (`_isLastBubbleBelowVisibleArea`): the FAB shows when the last
   * bubble's bottom extends more than a small threshold below the visible feed
   * area — not on raw `maxScrollExtent` — so it stays hidden whenever every
   * bubble is fully visible. Reads the last turn's host element from the typed
   * ViewChildren contract rather than a CSS-class query.
   */
  private isLastBubbleBelowFold(feed: HTMLElement): boolean {
    const last = this.items?.last?.hostElement;
    if (!last) {
      return false;
    }
    const bubbleBottom = last.offsetTop + last.offsetHeight;
    const visibleBottom = feed.scrollTop + feed.clientHeight;
    return bubbleBottom > visibleBottom + OrbiThreadComponent.BELOW_FOLD_PX;
  }
}
